import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "./LanguageContext";

/* ================= THEME ================= */
const COLORS = {
  ink: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.10)",
  bg: "#ffffff",
  overlay: "rgba(17,24,39,.55)",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 22px 70px rgba(0,0,0,.22)",
};

/* ================= ANIMS ================= */
const fadeIn = keyframes`
  from { opacity: 0; transform: translate3d(-50%, -50%, 0) scale(.98); }
  to   { opacity: 1; transform: translate3d(-50%, -50%, 0) scale(1); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translate3d(-50%, -50%, 0) scale(1); }
  to   { opacity: 0; transform: translate3d(-50%, -48%, 0) scale(.98); }
`;

/* ================= STYLES ================= */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${COLORS.overlay};
  z-index: 999998;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  @media (max-width: 768px) {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

const Card = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  width: min(460px, 92vw);
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: ${COLORS.shadow};
  z-index: 999999;
  overflow: hidden;

  animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)} 180ms ease forwards;
`;

const Header = styled.div`
  padding: 14px 14px 10px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid ${COLORS.line};
`;

const Title = styled.div`
  font-weight: 900;
  color: ${COLORS.ink};
  font-size: 15px;
  line-height: 1.2;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 18px;
  color: ${COLORS.ink};
  transition: transform .15s ease, box-shadow .15s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,.10); }
`;

const Body = styled.div`
  padding: 12px 14px 14px;
  display: grid;
  gap: 10px;
`;

const Message = styled.div`
  color: ${COLORS.dim};
  font-size: 13.5px;
  line-height: 1.45;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 2px;
`;

const Btn = styled.button`
  height: 38px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  color: ${COLORS.ink};
  font-weight: 900;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.10); }
`;

const Primary = styled(Btn)`
  background: ${COLORS.ink};
  color: #fff;
  border-color: ${COLORS.ink};
`;

/* ================= Component ================= */
export default function GoogleAuthPopup() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [payload, setPayload] = useState(null); // { flag, msg, title }
  const [closing, setClosing] = useState(false);

  const map = useMemo(() => {
    const t = (ka, en) => (language === "ka" ? ka : en);

    return {
      exists: {
        title: t("ანგარიში უკვე არსებობს", "Account already exists"),
        msg: t(
          "ეს ელფოსტა უკვე დარეგისტრირებულია. შედით ელფოსტით და პაროლით.",
          "This email is already registered. Please sign in with email and password."
        ),
      },
      error: {
        title: t("Google ავტორიზაციის შეცდომა", "Google auth error"),
        msg: t("გთხოვთ სცადეთ თავიდან.", "Please try again."),
      },
      cancel: {
        title: t("ავტორიზაცია შეწყდა", "Login cancelled"),
        msg: t("Google ავტორიზაცია შეწყდა.", "Google login was cancelled."),
      },
    };
  }, [language]);

  const close = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setPayload(null);
    }, 180);
  };

  // ESC close
  useEffect(() => {
    if (!payload) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    const readFlag = () => {
      // 1) sessionStorage flag (main)
      const ss = sessionStorage.getItem("googleAuthFlag");
      if (ss) return ss;

      // 2) URL param fallback (?google=exists|error|cancel)
      const sp = new URLSearchParams(location.search);
      const g = sp.get("google");
      return g || "";
    };

    const flag = readFlag();
    if (!flag) return;

    const cfg = map[flag];
    if (!cfg) {
      // cleanup unknown flags too
      try {
        sessionStorage.removeItem("googleAuthFlag");
        const sp = new URLSearchParams(location.search);
        if (sp.get("google")) {
          sp.delete("google");
          navigate(
            { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" },
            { replace: true }
          );
        }
      } catch {}
      return;
    }

    setPayload({ flag, ...cfg });

    // cleanup flags so refresh won't show again
    try {
      sessionStorage.removeItem("googleAuthFlag");

      const sp = new URLSearchParams(location.search);
      if (sp.get("google")) {
        sp.delete("google");
        navigate(
          { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" },
          { replace: true }
        );
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, location.pathname, location.search]);

  if (!payload) return null;

  return (
    <>
      <Overlay onClick={close} />
      <Card role="dialog" aria-modal="true" $closing={closing}>
        <Header>
          <div>
            <Title>{payload.title}</Title>
          </div>
          <CloseBtn onClick={close} aria-label="Close">×</CloseBtn>
        </Header>

        <Body>
          <Message>{payload.msg}</Message>

          <Actions>
            <Btn onClick={close}>{language === "ka" ? "დახურვა" : "Close"}</Btn>
            {payload.flag === "exists" ? (
              <Primary
                onClick={() => {
                  close();
                  navigate("/login");
                }}
              >
                {language === "ka" ? "შესვლა" : "Sign in"}
              </Primary>
            ) : null}
          </Actions>
        </Body>
      </Card>
    </>
  );
}